import React, { useEffect, useState, useRef } from "react";
import bgVideo from "../../assets/finance-bg.mp4";
import AOS from "aos";
import "aos/dist/aos.css";
import PageLoader from "../../components/PageLoader/PageLoader";
import ProgressBar from "../../components/ProgressBar/ProgressBar";
import logo from "../../assets/logo.png";
import { useNavigate } from "react-router-dom";
import { MdBarChart, MdEco, MdApps, MdLocationOn, MdPhone, MdEmail } from "react-icons/md";
import { GiWheat } from "react-icons/gi";
import { FaAppleAlt } from "react-icons/fa";

export const RippleButton = ({
    children,
    style = {},
    className = '',
    onClick,
    onMouseEnter,
    onMouseLeave
}) => {
    const handleClick = (e) => {
        const btn = e.currentTarget;
        const span = document.createElement('span');
        const sz = Math.max(btn.clientWidth, btn.clientHeight);
        const rect = btn.getBoundingClientRect();

        span.className = 'ripple-span';
        Object.assign(span.style, {
            width: `${sz}px`,
            height: `${sz}px`,
            left: `${e.clientX - rect.left - sz / 2}px`,
            top: `${e.clientY - rect.top - sz / 2}px`,
            position: 'absolute',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.25)',
            transform: 'scale(0)',
            animation: 'ripple 0.55s linear',
            pointerEvents: 'none',
        });

        btn.querySelectorAll('.ripple-span').forEach(s => s.remove());
        btn.appendChild(span);
        onClick && onClick(e);
    };

    return (
        <>
            <button
                className={`btn ${className}`}
                style={{ position: 'relative', overflow: 'hidden', ...style }}
                onClick={handleClick}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
            >
                {children}
            </button>
            <style>{`@keyframes ripple { to { transform: scale(4); opacity: 0; } }`}</style>
        </>
    );
};

const serviceConfig = {
    finance: { icon: MdBarChart, color: '#f97316', shadow: 'rgba(249,115,22,0.18)' },
    environnement: { icon: MdEco, color: '#22c55e', shadow: 'rgba(34,197,94,0.15)' },
    agriculture: { icon: GiWheat, color: '#84cc16', shadow: 'rgba(132,204,22,0.15)' },
    nutrition: { icon: FaAppleAlt, color: '#3b82f6', shadow: 'rgba(59,130,246,0.15)' },
};

const Home = () => {
    const [loading, setLoading] = useState(true);
    const videoRef = useRef(null);
    const navigate = useNavigate();

    const services = [
        { name: "Finance", path: "/finance", color: "#f97316", shadow: "rgba(249,115,22,0.18)" },
        { name: "Environnement", path: "/environnement", color: "#22c55e", shadow: "rgba(34,197,94,0.15)" },
        { name: "Agriculture", path: "/agriculture", color: "#84cc16", shadow: "rgba(132,204,22,0.15)" },
        { name: "Nutrition", path: "/nutrition", color: "#3b82f6", shadow: "rgba(59,130,246,0.15)" },
    ];

    useEffect(() => {
        const fn = () => {
            if (videoRef.current) {
                videoRef.current.style.transform = `scale(${1 + window.scrollY / 5000})`;
            }
        };
        window.addEventListener("scroll", fn, { passive: true });
        return () => window.removeEventListener("scroll", fn);
    }, []);

    useEffect(() => {
        AOS.init({ duration: 800, once: true, offset: 60 });
        const t = setTimeout(() => setLoading(false), 1400);
        return () => clearTimeout(t);
    }, []);

    return (
        <div style={{ fontFamily: "'DM Sans', sans-serif", background: '#0a0804', color: '#fff', height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <PageLoader loading={loading} />

            <header style={{
                position: "relative",
                flex: 1,
                minHeight: 0,
                overflow: "hidden",
                color: "#fff",
                display: "flex",
                flexDirection: "column",
            }}>
                {/* Ambient glows */}
                <div style={{
                    position: "absolute", bottom: -120, right: -120,
                    width: 480, height: 480, borderRadius: "50%",
                    background: "radial-gradient(circle, rgba(249,115,22,0.2) 0%, transparent 70%)",
                    zIndex: 0, pointerEvents: "none",
                }} />
                <div style={{
                    position: "absolute", top: -80, left: -80,
                    width: 300, height: 300, borderRadius: "50%",
                    background: "radial-gradient(circle, rgba(249,115,22,0.08) 0%, transparent 70%)",
                    zIndex: 0, pointerEvents: "none",
                }} />

                {/* Video */}
                <video
                    ref={videoRef}
                    autoPlay loop muted playsInline
                    style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
                >
                    <source src={bgVideo} type="video/mp4" />
                </video>

                {/* Overlay */}
                <div style={{
                    position: "absolute", inset: 0,
                    background: "linear-gradient(150deg, rgba(10,8,4,0.92) 0%, rgba(15,10,4,0.78) 45%, rgba(90,35,0,0.96) 100%)",
                }} />

                {/* Content */}
                <div style={{
                    position: "relative", zIndex: 2, flex: 1,
                    display: "flex", flexDirection: "column",
                    alignItems: "center", justifyContent: "center",
                    padding: "clamp(2rem, 6vh, 5rem) 1.5rem clamp(1.25rem, 3vh, 2rem)",
                    textAlign: "center",
                    overflowY: "auto",
                }}>
                    {/* Badge */}
                    <div data-aos="fade-down" style={{
                        display: "inline-flex", alignItems: "center", gap: 8,
                        padding: "5px 16px", borderRadius: 99,
                        background: "rgba(249,115,22,0.10)",
                        border: "1px solid rgba(249,115,22,0.28)",
                        fontSize: 10, letterSpacing: "0.14em",
                        color: "rgba(249,115,22,0.9)", fontWeight: 600,
                        marginBottom: "1.6rem",
                    }}>
                        <span style={{
                            width: 6, height: 6, borderRadius: "50%",
                            background: "#f97316",
                            boxShadow: "0 0 6px rgba(249,115,22,0.8)",
                        }} />
                        ESPACE SÉCURISÉ
                    </div>

                    {/* Logo */}
                    <div data-aos="zoom-in" data-aos-delay="100" style={{
                        width: 90, height: 90, borderRadius: "50%",
                        background: "rgba(249,115,22,0.08)",
                        border: "1px solid rgba(249,115,22,0.22)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        marginBottom: "1.6rem",
                        boxShadow: "0 0 40px rgba(249,115,22,0.12)",
                    }}>
                        <img src={logo} alt="Logo" style={{ width: 56, height: 56, objectFit: "contain" }} />
                    </div>

                    {/* Heading */}
                    <h1
                        style={{
                            fontSize: "clamp(2.5rem, 7vw, 5rem)",
                            // fontWeight: 800,
                            lineHeight: 1.1,
                        }}
                    >
                        <span style={{ color: "#fff" }}>Tumaini </span>
                        <span style={{ color: "#f97316" }}>Letu</span>

                        <span
                            style={{
                                background: "#f97316",
                                color: "#fff",
                                fontSize: "0.3em",
                                padding: "0.3em 0.8em",
                                borderRadius: "10px",
                                marginLeft: "1rem",
                                verticalAlign: "middle",
                                fontWeight: 800,
                            }}
                        >
                            ASBL
                        </span>
                    </h1>

                    {/* Divider accent */}
                    <div style={{
                        width: 36, height: 2, borderRadius: 2,
                        background: "rgba(249,115,22,0.55)",
                        margin: "0 auto 1.2rem",
                    }} />

                    {/* ✅ NEW TAGLINE */}
                    <div data-aos="fade-up" data-aos-delay="250" style={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        justifyContent: "center",
                        gap: "6px 10px",
                        marginBottom: "1.4rem",
                        maxWidth: 480,
                    }}>
                        {[
                            { text: "Protéger l'environnement", color: "#f97316" },
                            { text: "nourrir l'avenir", color: "#f97316" },
                            { text: "financer l'autonomie", color: "#f97316" },
                        ].map((item, i, arr) => (
                            <React.Fragment key={item.text}>
                                <span style={{
                                    fontSize: 16,
                                    fontWeight: 500,
                                    color: item.color,
                                    letterSpacing: "0.01em",
                                }}>
                                    {item.text}
                                </span>
                                {i < arr.length - 1 && (
                                    <span style={{
                                        width: 4, height: 4,
                                        borderRadius: "50%",
                                        background: "rgba(255,255,255,0.2)",
                                        display: "inline-block",
                                        flexShrink: 0,
                                        alignSelf: "center",
                                    }} />
                                )}
                            </React.Fragment>
                        ))}
                    </div>

                    <div style={{
                        width: 36, height: 2, borderRadius: 2,
                        background: "rgba(249,115,22,0.55)",
                        margin: "0 auto 1.2rem",
                    }} />

                    <p data-aos="fade-up" data-aos-delay="200" style={{
                        fontSize: "clamp(14px, 2vw, 16px)",
                        color: "rgba(255,255,255,0.50)",
                        maxWidth: 440,
                        lineHeight: 1.75,
                        fontWeight: 300,
                        marginBottom: "1.5rem",
                    }}>
                        Choisissez l'un de nos quatre services pour découvrir nos solutions et programmes adaptés à vos besoins.
                    </p>

                    {/* Services grid */}
                    <div data-aos="fade-up" data-aos-delay="300" style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(2, minmax(150px, 1fr))",
                        gap: "10px",
                        width: "100%",
                        maxWidth: 400,
                    }}>
                        {services.map((s, i) => (
                            <RippleButton
                                key={s.name}
                                onClick={() => navigate(s.path)}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = "translateY(-4px) scale(1.03)";
                                    e.currentTarget.style.background = s.shadow;
                                    e.currentTarget.style.borderColor = `${s.color}55`;
                                    e.currentTarget.style.boxShadow = `0 14px 32px ${s.shadow}`;
                                    e.currentTarget.querySelector('.btn-arrow').style.opacity = "0.7";
                                    e.currentTarget.querySelector('.btn-arrow').style.transform = "translateX(0)";
                                    e.currentTarget.querySelector('.color-bar').style.opacity = "1";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = "translateY(0) scale(1)";
                                    e.currentTarget.style.background = "rgba(255,255,255,0.055)";
                                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.10)";
                                    e.currentTarget.style.boxShadow = "none";
                                    e.currentTarget.querySelector('.btn-arrow').style.opacity = "0";
                                    e.currentTarget.querySelector('.btn-arrow').style.transform = "translateX(-4px)";
                                    e.currentTarget.querySelector('.color-bar').style.opacity = "0.5";
                                }}
                                style={{
                                    padding: "1rem",
                                    borderRadius: 14,
                                    background: "rgba(255,255,255,0.055)",
                                    border: "1px solid rgba(255,255,255,0.10)",
                                    color: "#fff",
                                    fontSize: 13,
                                    fontWeight: 600,
                                    backdropFilter: "blur(20px)",
                                    transition: "all 0.28s cubic-bezier(0.34,1.4,0.64,1)",
                                    cursor: "pointer",
                                    textAlign: "left",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                    gap: 6,
                                }}
                            >
                                {/* Top color bar */}
                                <span
                                    className="color-bar"
                                    style={{
                                        display: "block",
                                        width: 22,
                                        height: 3,
                                        borderRadius: 2,
                                        background: s.color,
                                        opacity: 0.5,
                                        transition: "opacity 0.2s",
                                        marginBottom: 2,
                                    }}
                                />
                                {s.name}
                                {/* Arrow hint */}
                                <span
                                    className="btn-arrow"
                                    style={{
                                        position: "absolute",
                                        top: 10, right: 12,
                                        fontSize: 12,
                                        opacity: 0,
                                        transform: "translateX(-4px)",
                                        transition: "all 0.22s ease",
                                    }}
                                >
                                    →
                                </span>
                            </RippleButton>
                        ))}
                    </div>

                    {/* ✅ VALEURS */}
                    <div data-aos="fade-up" data-aos-delay="400" style={{
                        marginTop: "1.6rem",
                        width: "100%",
                        maxWidth: 420,
                    }}>
                        <p style={{
                            fontSize: 10,
                            letterSpacing: "0.16em",
                            color: "rgba(255,255,255,0.25)",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            marginBottom: "0.9rem",
                            textAlign: "center",
                        }}>
                            Nos Valeurs
                        </p>

                        <div style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "8px",
                            justifyContent: "center",
                        }}>
                            {[
                                { label: "Transparence", color: "#3b82f6" },
                                { label: "Accessibilité", color: "#3b82f6" },
                                { label: "Inclusion financière", color: "#3b82f6" },
                            ].map((v) => (
                                <span key={v.label} style={{
                                    padding: "5px 13px",
                                    borderRadius: 99,
                                    border: `1px solid ${v.color}33`,
                                    background: `${v.color}0f`,
                                    color: v.color,
                                    fontSize: 12,
                                    fontWeight: 500,
                                    letterSpacing: "0.02em",
                                    transition: "all 0.2s ease",
                                }}>
                                    {v.label}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </header>

            {/* ============ FOOTER ============ */}
            <footer style={{
                position: "relative",
                flexShrink: 0,
                background: "linear-gradient(180deg, #0a0804 0%, #0d0905 100%)",
                borderTop: "1px solid rgba(255,255,255,0.07)",
            }}>
                <div style={{
                    maxWidth: 1080, margin: "0 auto",
                    padding: "0.85rem 1.5rem",
                    display: "flex", flexWrap: "wrap",
                    alignItems: "center", justifyContent: "space-between",
                    gap: "8px 24px",
                }}>
                    {/* Brand */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{
                            width: 24, height: 24, borderRadius: "50%", flexShrink: 0,
                            background: "rgba(249,115,22,0.08)",
                            border: "1px solid rgba(249,115,22,0.22)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                            <img src={logo} alt="Logo" style={{ width: 14, height: 14, objectFit: "contain" }} />
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}>
                            Tumaini Letu <span style={{ color: "#f97316" }}>ASBL</span>
                        </span>
                    </div>

                    {/* Contact, inline */}
                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px 18px" }}>
                        <span
                            title="Avenue du Port, Quartier Les Volcans, N° 5, Commune de Goma, Goma, Nord-Kivu, RDC"
                            style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "rgba(255,255,255,0.5)", whiteSpace: "nowrap" }}
                        >
                            <MdLocationOn style={{ color: "#f97316", fontSize: 13 }} />
                            Goma, Nord-Kivu, RDC
                        </span>
                        <a
                            href="tel:+243803140752"
                            onMouseEnter={(e) => (e.currentTarget.style.color = "#f97316")}
                            onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.5)")}
                            style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "rgba(255,255,255,0.5)", textDecoration: "none", whiteSpace: "nowrap", transition: "color 0.2s ease" }}
                        >
                            <MdPhone style={{ color: "#f97316", fontSize: 13 }} />
                            +243 803 140 752
                        </a>
                        <a
                            href="mailto:info@tumainiletu.com"
                            onMouseEnter={(e) => (e.currentTarget.style.color = "#f97316")}
                            onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.5)")}
                            style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "rgba(255,255,255,0.5)", textDecoration: "none", whiteSpace: "nowrap", transition: "color 0.2s ease" }}
                        >
                            <MdEmail style={{ color: "#f97316", fontSize: 13 }} />
                            info@tumainiletu.com
                        </a>
                    </div>

                    {/* Copyright */}
                    <span style={{ fontSize: 10.5, color: "rgba(255,255,255,0.18)", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>
                        © {new Date().getFullYear()} TUMAINI LETU — TOUS DROITS RÉSERVÉS
                    </span>
                </div>
            </footer>
        </div>
    );
};

export default Home;
